class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.setupPopupInteractions();

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  connectedCallback() {
    this.setupOutsideDismissProtection();
    this.editorPreviewFrame = requestAnimationFrame(() => {
      this.editorPreviewFrame = null;
      if (this.isConnected) this.initializeEditorPreview();
    });
  }

  getLineSpacingTargets() {
    return this.querySelectorAll('.he-bc-heading, .he-bc-message, .he-bc-product-title, .he-bc-price, .he-bc-option dt, .he-bc-option dd');
  }

  preparePriceCurrencyGap(target) {
    if (!target.matches('.he-bc-price') || target.querySelector('.he-bc-currency-symbol')) return;
    // Wrap only the first visible-text dollar sign; preserve the formatter, amount and markup.
    const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const index = node.nodeValue.indexOf('$');
      if (index < 0) continue;
      const symbolText = node.splitText(index);
      symbolText.splitText(1);
      const symbol = document.createElement('span');
      symbol.className = 'he-bc-currency-symbol';
      symbol.textContent = symbolText.nodeValue;
      symbolText.replaceWith(symbol);
      break;
    }
  }

  prepareLineSpacingBlocks() {
    // Popup-only row cap and optional editor-only four-row styling sample.
    const fourthPreview = this.editorPreviewPinned && window.Shopify?.designMode === true
      && this.dataset.heBasketPreviewFourth === 'true';
    this.querySelectorAll('.he-bc-options').forEach((list) => {
      const optionRows = () => Array.from(list.children).filter((row) => row.classList.contains('he-bc-option'));
      optionRows().filter((row) => row.dataset.heBcFourthSample === 'true').forEach((row) => row.remove());
      if (fourthPreview) {
        optionRows().slice(3).forEach((row) => row.remove());
        while (optionRows().length < 4) {
          const number = optionRows().length + 1;
          const row = document.createElement('div');
          row.className = 'he-bc-option';
          row.dataset.heBcFourthSample = 'true';
          const label = document.createElement('dt');
          const value = document.createElement('dd');
          label.textContent = number === 4 ? 'Preview:' : 'Sample option ' + number + ':';
          value.textContent = number === 4 ? 'Fourth variation' : 'Preview value';
          row.append(label, value);
          list.appendChild(row);
        }
      }
      optionRows().slice(4).forEach((row) => row.remove());
      list.dataset.heBcRowCount = String(optionRows().length);
    });
    this.getLineSpacingTargets().forEach((target) => {
      if (getComputedStyle(target).display === 'none' || !target.textContent.trim()) return;
      this.preparePriceCurrencyGap(target);
      const existing = Array.from(target.children).find((child) => child.classList.contains('he-bc-live-text'));
      if (existing) return;
      const live = document.createElement('span');
      live.className = 'he-bc-live-text';
      while (target.firstChild) live.appendChild(target.firstChild);
      const pin = document.createElement('span');
      pin.className = 'he-bc-baseline-pin';
      pin.setAttribute('aria-hidden', 'true');
      live.prepend(pin);
      // An inaccessible, in-flow sizing copy preserves the original text frame,
      // natural wrapping, and first baseline at zero added line spacing.
      const sizer = live.cloneNode(true);
      sizer.className = 'he-bc-text-sizer';
      sizer.setAttribute('aria-hidden', 'true');
      sizer.setAttribute('inert', '');
      sizer.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      sizer.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
      target.classList.add('he-bc-isolated-text');
      target.append(live, sizer);
    });
  }

  observeLineSpacingAnchors() {
    this.lineSpacingObserver?.disconnect();
    this.prepareLineSpacingBlocks();
    this.syncLineSpacingAnchors();
    if (typeof ResizeObserver !== 'function') return;
    if (!this.lineSpacingObserver) {
      this.lineSpacingObserver = new ResizeObserver(() => this.syncLineSpacingAnchors());
    }
    this.getLineSpacingTargets().forEach((target) => {
      this.lineSpacingObserver.observe(target);
      const live = Array.from(target.children).find((child) => child.classList.contains('he-bc-live-text'));
      if (live) this.lineSpacingObserver.observe(live);
    });
  }

  syncLineSpacingAnchors() {
    if (!this.lineSpacingCounts) this.lineSpacingCounts = new WeakMap();
    this.getLineSpacingTargets().forEach((target) => {
      if (getComputedStyle(target).display === 'none') return;
      const children = Array.from(target.children);
      const live = children.find((child) => child.classList.contains('he-bc-live-text'));
      const sizer = children.find((child) => child.classList.contains('he-bc-text-sizer'));
      if (!live || !sizer) return;
      const actualPin = live.querySelector('.he-bc-baseline-pin');
      const referencePin = sizer.querySelector('.he-bc-baseline-pin');
      if (!actualPin || !referencePin) return;
      const liveStyle = getComputedStyle(live);
      const layoutHeight = parseFloat(liveStyle.height);
      const visualHeight = live.getBoundingClientRect().height;
      const scaleY = layoutHeight > 0 ? visualHeight / layoutHeight : 1;
      if (!(scaleY > 0)) return;
      const previous = parseFloat(live.style.getPropertyValue('--he-bc-ink-shift')) || 0;
      const next = previous + (referencePin.getBoundingClientRect().top - actualPin.getBoundingClientRect().top) / scaleY;
      const value = Math.round(next * 1000000) / 1000000 + 'px';
      if (live.style.getPropertyValue('--he-bc-ink-shift') !== value) {
        live.style.setProperty('--he-bc-ink-shift', value);
      }
      const referenceStyle = getComputedStyle(sizer);
      const lineHeight = parseFloat(referenceStyle.lineHeight);
      const referenceHeight = parseFloat(referenceStyle.height);
      const count = lineHeight > 0 && Number.isFinite(referenceHeight) ? Math.max(1, Math.round(referenceHeight / lineHeight)) : 1;
      this.lineSpacingCounts.set(target, count);
    });
    // Later option rows form one text group, but their relative offsets never
    // increase the container's layout height or move price/image/button elements.
    const delta = parseFloat(getComputedStyle(this).getPropertyValue('--he-bc-options-line-fine')) || 0;
    let precedingLines = 0;
    this.querySelectorAll('.he-bc-option').forEach((row) => {
      const value = Math.round(precedingLines * delta * 1000000) / 1000000 + 'px';
      if (row.style.getPropertyValue('--he-bc-options-row-shift') !== value) {
        row.style.setProperty('--he-bc-options-row-shift', value);
      }
      let lines = 1;
      row.querySelectorAll('dt, dd').forEach((field) => {
        if (getComputedStyle(field).display !== 'none') lines = Math.max(lines, this.lineSpacingCounts.get(field) || 1);
      });
      precedingLines += lines;
    });
  }

  initializeEditorPreview() {
    // Two independent guards keep this styling aid out of real storefront visits.
    if (this.dataset.heBasketEditorPreview !== 'true' || window.Shopify?.designMode !== true) return;
    const template = this.querySelector('template[data-he-bc-preview-content]');
    const content = this.querySelector('#cart-notification-product');
    if (!template || !content) return;
    content.replaceChildren(template.content.cloneNode(true));
    const heading = this.querySelector('#he-art-basket-heading');
    if (heading) {
      const format = this.dataset.heBasketHeading || '{count} {items} added to your basket';
      heading.textContent = format.replaceAll('{count}', '1').replaceAll('{items}', 'item');
    }
    this.editorPreviewPinned = true;
    this.clearAutoClose();
    this.notification.setAttribute('aria-modal', 'false');
    this.syncCheckoutAppearance();
    this.observeLineSpacingAnchors();
    this.notification.classList.add('animate', 'active');
    this.header?.reveal?.();
    this.startBagAlignment();
    // No cart requests, cart mutations, analytics event, forced focus or body-click trap.
  }

  open() {
    this.clearAutoClose();
    this.syncCheckoutAppearance();
    this.observeLineSpacingAnchors();
    this.notification.classList.add('animate', 'active');
    this.startBagAlignment();
    if (this.editorPreviewPinned) return;
    // Keep keyboard access without asking the browser to scroll to the header.
    removeTrapFocus();
    this.notification.focus({ preventScroll: true });
    document.body.addEventListener('click', this.onBodyClick);
    this.scheduleAutoClose();
    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    if (this.editorPreviewPinned) return;
    this.lineSpacingObserver?.disconnect();
    this.clearAutoClose();
    const restoreFocus = this.notification.contains(document.activeElement);
    this.notification.classList.remove('active');
    this.stopBagAlignment();
    document.body.removeEventListener('click', this.onBodyClick);
    this.popupHovered = false;
    this.popupPressed = false;
    if (restoreFocus && this.activeElement?.isConnected) {
      this.activeElement.focus({ preventScroll: true });
    }
  }

  scrollToMobilePopupAfterAdd() {
    if (!this.isConnected || this.editorPreviewPinned || this.dataset.heBasketMobileScroll !== 'true') return;
    if (!window.matchMedia('(max-width: 770px)').matches || !(window.scrollY > 0)) return;
    // Reveal the header popup immediately, before its normal three-second timer starts.
    // This runs only from a successful item's render, never from preview initialization.
    window.scrollTo({ top: 0, left: window.scrollX, behavior: 'instant' });
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    const count = this.pendingAddedQuantity || 1;
    const heading = this.querySelector('#he-art-basket-heading');
    if (heading) {
      const format = this.dataset.heBasketHeading || '{count} {items} added to your basket';
      heading.textContent = format.replaceAll('{count}', String(count)).replaceAll('{items}', count === 1 ? 'item' : 'items');
    }
    this.pendingAddedQuantity = null;
    if (this.header) this.header.reveal();
    if (parsedState.key) this.scrollToMobilePopupAfterAdd();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  // Sample a hidden, non-interactive clone so a hovered/loading source does not
  // contaminate the normal style. The real product button and its handlers stay untouched.
  syncCheckoutAppearance() {
    if (this.dataset.heBasketMatch !== 'true') return;
    const origin = this.activeElement?.closest('[id^="MainProduct-"]');
    const product = origin || document.querySelector('[id^="MainProduct-"]');
    const source = product?.querySelector('.pdp-basket .product-form__submit');
    if (!source) return;
    const probe = source.cloneNode(false);
    probe.removeAttribute('id'); probe.removeAttribute('disabled');
    probe.removeAttribute('name'); probe.type = 'button'; probe.tabIndex = -1;
    probe.classList.remove('loading', 'focused');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position:fixed!important;top:-10000px!important;left:0!important;visibility:hidden!important;pointer-events:none!important;';
    const label = document.createElement('span');
    probe.appendChild(label);
    source.parentElement.appendChild(probe);
    const set = (name, value) => { if (value) this.notification.style.setProperty('--he-bc-source-' + name, value); };
    try {
      const normal = getComputedStyle(probe);
      // The visible basket label is its span's ::after, not the hidden native text.
      const visibleLabel = getComputedStyle(label, '::after');
      for (const property of ['font-family','font-size','font-weight','font-style','font-stretch','font-variant','font-feature-settings','font-variation-settings','font-kerning','font-optical-sizing','letter-spacing','line-height','text-transform','text-shadow','-webkit-text-stroke-width','-webkit-text-stroke-color']) {
        set('label-' + property, visibleLabel.getPropertyValue(property));
      }
      set('background', normal.backgroundColor); set('color', normal.color); set('transition', normal.transition);
      set('border-color', normal.borderTopColor); set('border-width', normal.borderTopWidth);
      const variables = getComputedStyle(probe);
      const resolve = value => value.replace(/var\((--[a-zA-Z0-9_-]+)\)/g, (_, name) => variables.getPropertyValue(name).trim());
      const inspect = rules => {
        for (const rule of rules) {
          if (rule.media && !matchMedia(rule.media.mediaText).matches) continue;
          if (rule.cssRules) { inspect(rule.cssRules); continue; }
          if (!rule.selectorText?.includes('#' + product.id + ' .pdp-basket .product-form__submit:not(:disabled):hover')) continue;
          for (const [property, target] of [['background','hover-background'],['background-color','hover-background'],['color','hover-color'],['border-color','hover-border-color'],['border-width','hover-border-width']]) {
            set(target, resolve(rule.style.getPropertyValue(property).trim()));
          }
        }
      };
      for (const sheet of document.styleSheets) { try { inspect(sheet.cssRules); } catch (_) { /* Cross-origin fonts need no inspection. */ } }
    } finally { probe.remove(); }
  }

  setupPopupInteractions() {
    this.notification.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
        this.popupHovered = true;
        this.clearAutoClose();
      }
    });
    this.notification.addEventListener('pointerleave', (event) => {
      const wasHovering = this.popupHovered;
      this.popupHovered = false;
      if (wasHovering && (event.pointerType === 'mouse' || event.pointerType === 'pen')) {
        this.close();
      } else {
        // Touch has no hover-out gesture; preserve its existing idle timer.
        this.scheduleAutoClose();
      }
    });
    this.notification.addEventListener('pointerdown', () => {
      this.popupPressed = true;
      this.clearAutoClose();
    });
    const release = () => {
      this.popupPressed = false;
      this.scheduleAutoClose();
    };
    this.notification.addEventListener('pointerup', release);
    this.notification.addEventListener('pointercancel', release);
    this.notification.addEventListener('focusin', (event) => {
      if (event.target !== this.notification) this.clearAutoClose();
    });
    this.notification.addEventListener('focusout', () => {
      queueMicrotask(() => this.scheduleAutoClose());
    });
    this.notification.addEventListener('keydown', (event) => {
      if (this.editorPreviewPinned || event.key !== 'Tab') return;
      this.clearAutoClose();
      const controls = getFocusableElements(this.notification).filter(element => element.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      const focused = document.activeElement;
      if (event.shiftKey && (focused === first || focused === this.notification)) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && (focused === last || focused === this.notification)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    });
  }

  clearAutoClose() {
    clearTimeout(this.autoCloseTimer);
    this.autoCloseTimer = null;
  }

  scheduleAutoClose() {
    this.clearAutoClose();
    if (this.editorPreviewPinned) return;
    if (!this.isConnected || !this.notification.classList.contains('active')) return;
    const focusedControl = this.notification.contains(document.activeElement)
      && document.activeElement !== this.notification;
    if (this.popupHovered || this.popupPressed || focusedControl) return;
    this.autoCloseTimer = setTimeout(() => {
      this.autoCloseTimer = null;
      const usingControl = this.notification.contains(document.activeElement)
        && document.activeElement !== this.notification;
      if (!this.popupHovered && !this.popupPressed && !usingControl) this.close();
    }, 3000);
  }

  // Map the native path through its centered viewBox without the SVG hover transform.
  // The stable wrapper still carries each brand's real header position and base scale.
  getStableBagRect(bag) {
    try {
      const svg = bag.ownerSVGElement;
      const wrapper = svg?.parentElement?.closest('.svg-wrapper');
      const view = svg?.viewBox?.baseVal;
      if (!wrapper || !view || !(view.width > 0 && view.height > 0)) return null;
      const frameRect = wrapper.getBoundingClientRect();
      const frameWidth = wrapper.offsetWidth;
      const frameHeight = wrapper.offsetHeight;
      if (!(frameWidth > 0 && frameHeight > 0 && frameRect.width > 0 && frameRect.height > 0)) return null;
      const svgStyle = getComputedStyle(svg);
      const viewportWidth = parseFloat(svgStyle.width);
      const viewportHeight = parseFloat(svgStyle.height);
      if (!(viewportWidth > 0 && viewportHeight > 0)) return null;
      const box = bag.getBBox();
      const unit = Math.min(viewportWidth / view.width, viewportHeight / view.height);
      const scaleX = frameRect.width / frameWidth;
      const scaleY = frameRect.height / frameHeight;
      const left = frameRect.left + ((frameWidth - view.width * unit) / 2 + (box.x - view.x) * unit) * scaleX;
      const top = frameRect.top + ((frameHeight - view.height * unit) / 2 + (box.y - view.y) * unit) * scaleY;
      const width = box.width * unit * scaleX;
      const height = box.height * unit * scaleY;
      if (![left, top, width, height].every(Number.isFinite) || !(width > 0 && height > 0)) return null;
      return { left, top, right: left + width, bottom: top + height, width, height };
    } catch (error) {
      // A cart refresh can briefly detach its old SVG. Retry on the next alignment frame.
      return null;
    }
  }

  // Follow unhovered bag artwork independently of the header divider.
  // Compact mode aligns right edges; mobile keeps its adjustable equal outside inset.
  startBagAlignment() {
    this.stopBagAlignment();
    this.bagOriginalRight = this.notification.style.right;
    this.bagOriginalTop = this.notification.style.top;
    const desktop = window.matchMedia('(min-width: 771px)');
    let frame = 0;
    let stopped = false;
    const align = () => {
      if (stopped || !this.isConnected || !this.notification.classList.contains('active')) return;
      const owner = this.closest('.section-header') || document;
      const bag = owner.querySelector('#cart-icon-bubble svg.icon-cart path, #cart-icon-bubble svg.icon-cart-empty path');
      if (!bag) return;
      const bagRect = this.getStableBagRect(bag);
      if (!bagRect) return;
      const popupRect = this.notification.getBoundingClientRect();
      if (!(bagRect.width > 0 && popupRect.width > 0 && popupRect.height > 0)) return;
      const style = getComputedStyle(this.notification);
      let layoutWidth = parseFloat(style.width);
      let layoutHeight = parseFloat(style.height);
      if (style.boxSizing !== 'border-box') {
        layoutWidth += (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0)
          + (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0);
        layoutHeight += (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0)
          + (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
      }
      const gapValue = parseFloat(style.getPropertyValue('--he-bc-popup_gap'));
      const mobileYValue = parseFloat(style.getPropertyValue('--he-bc-mobile-y'));
      const mobileY = !desktop.matches && Number.isFinite(mobileYValue) ? mobileYValue : 0;
      const gap = (Number.isFinite(gapValue) ? gapValue : 40) + mobileY;
      if (layoutHeight > 0) {
        const scaleY = popupRect.height / layoutHeight;
        const differenceY = (popupRect.top - bagRect.bottom - gap) / scaleY;
        if (Math.abs(differenceY) > 0.05) {
          const currentTop = parseFloat(style.top) || 0;
          this.notification.style.top = (currentTop - differenceY).toFixed(3) + 'px';
        }
      }
      if (!desktop.matches) {
        this.notification.style.right = this.bagOriginalRight;
      } else if (layoutWidth > 0) {
        const scaleX = popupRect.width / layoutWidth;
        const differenceX = (popupRect.right - bagRect.right) / scaleX;
        if (Math.abs(differenceX) > 0.05) {
          const currentRight = parseFloat(style.right) || 0;
          this.notification.style.right = (currentRight + differenceX).toFixed(3) + 'px';
        }
      }
    };
    const tick = () => {
      frame = 0;
      if (stopped || !this.isConnected || !this.notification.classList.contains('active')) return;
      align();
      frame = requestAnimationFrame(tick);
    };
    const schedule = () => { if (!stopped && !frame) frame = requestAnimationFrame(tick); };
    const observer = new ResizeObserver(schedule);
    const owner = this.closest('.section-header');
    const cart = owner?.querySelector('#cart-icon-bubble');
    [owner, cart, this.notification, this.querySelector('.cart-notification-wrapper')].filter(Boolean)
      .forEach(element => observer.observe(element));
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.visualViewport?.addEventListener('resize', schedule);
    desktop.addEventListener('change', schedule);
    document.fonts?.addEventListener('loadingdone', schedule);
    document.fonts?.ready.then(schedule);
    this.bagAlignmentCleanup = () => {
      stopped = true; cancelAnimationFrame(frame); observer.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      window.visualViewport?.removeEventListener('resize', schedule);
      desktop.removeEventListener('change', schedule);
      document.fonts?.removeEventListener('loadingdone', schedule);
    };
    align(); schedule();
  }

  stopBagAlignment() {
    this.bagAlignmentCleanup?.();
    this.bagAlignmentCleanup = null;
    if (this.bagOriginalRight !== undefined) {
      this.notification.style.right = this.bagOriginalRight;
      this.bagOriginalRight = undefined;
    }
    if (this.bagOriginalTop !== undefined) {
      this.notification.style.top = this.bagOriginalTop;
      this.bagOriginalTop = undefined;
    }
  }

  disconnectedCallback() {
    this.outsideDismissCleanup?.();
    this.lineSpacingObserver?.disconnect();
    cancelAnimationFrame(this.editorPreviewFrame);
    this.clearAutoClose();
    this.stopBagAlignment();
    document.body.removeEventListener('click', this.onBodyClick);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  // Consume the dismissal click BEFORE product-image/link target handlers run.
  // Applies at every viewport width; the existing 770px layout stays unchanged.
  setupOutsideDismissProtection() {
    this.outsideDismissCleanup?.();
    let pendingOutsideTap = false;
    const inside = (event) => event.composedPath?.().includes(this.notification)
      || this.notification.contains(event.target);
    const protectNow = () => this.isConnected && !this.editorPreviewPinned
      && this.notification.classList.contains('active');
    const down = (event) => {
      // Every new press resets the previous gesture, including after dismissal.
      pendingOutsideTap = false;
      if (event.isPrimary === false || event.button !== 0 || !protectNow() || inside(event)) return;
      pendingOutsideTap = true;
      // Keep the idle timeout from expiring between this press and its click.
      // Do not cancel pointer/touch scrolling, selection or browser gestures.
      this.clearAutoClose();
    };
    const release = () => {
      if (pendingOutsideTap) this.scheduleAutoClose();
    };
    const cancel = () => {
      if (!pendingOutsideTap) return;
      pendingOutsideTap = false;
      this.scheduleAutoClose();
    };
    const click = (event) => {
      // Keyboard/programmatic activation retains its existing behavior.
      if (event.detail === 0 || event.button !== 0) return;
      const dismiss = pendingOutsideTap || protectNow();
      pendingOutsideTap = false;
      if (!dismiss || !this.isConnected || this.editorPreviewPinned || inside(event)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.close();
    };
    // Capture on window precedes target handlers and native link activation.
    // Keep the gesture guard alive if the popup closes between down and click.
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('pointerup', release, true);
    window.addEventListener('pointercancel', cancel, true);
    window.addEventListener('click', click, { capture: true, passive: false });
    this.outsideDismissCleanup = () => {
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointerup', release, true);
      window.removeEventListener('pointercancel', cancel, true);
      window.removeEventListener('click', click, true);
      pendingOutsideTap = false;
      this.outsideDismissCleanup = null;
    };
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
    const form = element?.closest('form');
    const quantity = Number(form?.elements.namedItem('quantity')?.value || 1);
    this.pendingAddedQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }
}

customElements.define('cart-notification', CartNotification);




















